import {
  Box,
  ButtonBase,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Company,
  Contact,
  Department,
  FirewallRecord,
  Log,
  PopulatedAPI,
  Product,
  Ticket,
  User,
  Webhook,
} from "../../services/swagger";
import AvatarWithInitials from "../atoms/AvatarWithInitials";
import DateAndName from "../molecules/DateAndName";
import ApartmentIcon from "@mui/icons-material/Apartment";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import TicketStatus from "../atoms/TicketStatus";
import PriorityChip from "../atoms/PriorityChip";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../helpers/string";
import useBreakpoint from "../../hooks/useBreakpoint";
import UserRoleChip from "../atoms/UserRoleChip";
import EmailIcon from "@mui/icons-material/Email";
import useEnums from "../../hooks/useEnums";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaidIcon from "@mui/icons-material/Paid";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import BarChartIcon from "@mui/icons-material/BarChart";
import LinkIcon from "@mui/icons-material/Link";
import { BusinessCenter, Phone, SensorOccupied } from "@mui/icons-material";

interface ItemCardProps {
  ticket?: Ticket;
  user?: User;
  log?: Log;
  department?: Department;
  record?: FirewallRecord;
  product?: Product;
  api?: PopulatedAPI;
  webhook?: Webhook;
  company?: Company;
  contact?: Contact;
  onClick?: () => void;
}

function ItemCardTitle(props: ItemCardProps) {
  const {
    ticket,
    user,
    department,
    record,
    product,
    webhook,
    api,
    contact,
    company,
  } = props;
  const breakpoint = useBreakpoint();

  if (ticket)
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <AvatarWithInitials size={breakpoint.md ? undefined : 22}>
          {ticket.user?.name || ticket.person || ""}
        </AvatarWithInitials>
        <Typography
          align="left"
          fontWeight={(ticket as any).unRead ? "bold" : undefined}
        >
          {ticket.title}
        </Typography>
      </Stack>
    );
  if (user)
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <AvatarWithInitials size={breakpoint.md ? undefined : 22}>
          {user.name}
        </AvatarWithInitials>
        <Typography>{user.username}</Typography>
      </Stack>
    );
  if (department)
    return <Typography align="left">{department.name}</Typography>;
  if (record) return <Typography align="left">{record.ip}</Typography>;
  if (product) return <Typography align="left">{product.name}</Typography>;
  if (webhook)
    return <Typography align="left">{webhook.description}</Typography>;
  if (api) return <Typography align="left">{api.description}</Typography>;
  if (contact)
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <AvatarWithInitials size={breakpoint.md ? undefined : 22}>
          {`${contact.firstName} ${contact.lastName}`}
        </AvatarWithInitials>
        <Typography>{`${contact.firstName} ${contact.lastName}`}</Typography>
      </Stack>
    );
  if (company)
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <AvatarWithInitials size={breakpoint.md ? undefined : 22}>
          {company.name}
        </AvatarWithInitials>
        <Typography>{company.name}</Typography>
      </Stack>
    );
}

function DesktopCardBody(props: ItemCardProps) {
  const {
    ticket,
    user,
    log,
    department,
    record,
    product,
    webhook,
    api,
    company,
    contact,
  } = props;
  const { recordTypeObj } = useEnums();

  if (ticket)
    return (
      <Stack direction={"row"} alignItems={"center"} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PriorityChip priority={ticket.priority} />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <ApartmentIcon sx={{ color: "primary.main" }} />
          <Typography align="left">{ticket.companyName}</Typography>
        </Stack>
        {ticket.department?.name && (
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupOutlinedIcon sx={{ color: "primary.main" }} />
            <Typography align="left">{ticket.department?.name}</Typography>
          </Stack>
        )}
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            <TicketStatus status={ticket.status} />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <LocationOnIcon sx={{ color: "text.secondary" }} />
            <Typography
              align="left"
              maxWidth={150}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {[ticket.city, ticket.location].filter((e) => e).join(", ")}
            </Typography>
          </Stack>
        </>
      </Stack>
    );
  if (user)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Typography align="left">{user.name}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonIcon sx={{ color: "text.secondary" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {user.username}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ApartmentIcon sx={{ color: "primary.main" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {user.companyName}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          fontWeight="bold"
        >
          <EmailIcon />
          <Typography align="left">{user.email}</Typography>
        </Stack>
      </Stack>
    );
  if (log)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonIcon sx={{ color: "text.secondary" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {log.user?.username || log.username}
          </Typography>
        </Stack>
        <Typography align="left">
          {log.action || `${log.object} ${(log as any).type}`}
        </Typography>
      </Stack>
    );
  if (department)
    return (
      <Typography align="left" color="text.secondary">
        {department.description}
      </Typography>
    );
  if (record)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Typography align="left" color="text.secondary">
          {record.ip}/{record.prefix}
        </Typography>
        <Typography align="left" color="text.secondary" fontWeight="bold">
          {recordTypeObj[record.type]}
        </Typography>
      </Stack>
    );
  if (product)
    return (
      <Stack spacing={1}>
        <Stack alignItems={"flex-start"} direction={"row"} spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InventoryIcon sx={{ color: "text.secondary" }} />
            <Typography align="left" color="primary.main" fontWeight="bold">
              {product.group}
            </Typography>
          </Stack>
          {product.currency && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PaidIcon sx={{ color: "success.main" }} />
              <Typography align="left" fontWeight="bold" color="text.secondary">
                {`${product.price.toFixed(2)} ${product.currency}`}
              </Typography>
            </Stack>
          )}
        </Stack>
        {product.source === "SAP" && (
          <Stack alignItems={"flex-start"} direction={"row"} spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ProductionQuantityLimitsIcon sx={{ color: "text.secondary" }} />
              <Typography align="left" fontWeight="bold" color="text.secondary">
                x{product.totalQuantityInStock}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <BarChartIcon sx={{ color: "text.secondary" }} />
              <Typography align="left" fontWeight="bold" color="text.secondary">
                {product.totalAmountUsed}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  if (api)
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <PersonIcon sx={{ color: "primary.main" }} />
        <Typography
          align="left"
          maxWidth={150}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {api.user?.name}
        </Typography>
      </Stack>
    );
  if (webhook)
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <LinkIcon sx={{ color: "primary.main" }} />
        <Typography
          align="left"
          maxWidth={300}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {webhook.url}
        </Typography>
      </Stack>
    );
  if (company)
    return (
      <Stack spacing={1}>
        <Stack alignItems={"flex-start"} direction={"row"} spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <BusinessCenter sx={{ color: "text.secondary" }} />
            <Typography align="left" color="primary.main" fontWeight="bold">
              {company.regNo}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <EmailIcon sx={{ color: "text.secondary" }} />
            <Typography align="left" fontWeight="bold" color="text.secondary">
              {company.emails.length}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Phone sx={{ color: "text.secondary" }} />
            <Typography align="left" fontWeight="bold" color="text.secondary">
              {company.phones.length}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  if (contact)
    return (
      <Stack spacing={1}>
        <Stack alignItems={"flex-start"} direction={"row"} spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EmailIcon sx={{ color: "primary" }} />
            <Typography align="left" color="primary.main" fontWeight="bold">
              {contact.email}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Phone sx={{ color: "text.secondary" }} />
            <Typography
              align="left"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                maxWidth: 100,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {contact.phones.join(", ")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <SensorOccupied sx={{ color: "text.secondary" }} />
            <Typography
              align="left"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                maxWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {contact.position}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
}

function MobileCardBody(props: ItemCardProps) {
  const {
    ticket,
    user,
    log,
    department,
    record,
    product,
    api,
    webhook,
    company,
    contact,
  } = props;
  const { recordTypeObj } = useEnums();

  if (ticket)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonIcon sx={{ color: "text.secondary" }} />
          <Typography align="left" color="text.secondary" fontWeight="bold">
            {ticket.user?.name || ticket.person || ""}
          </Typography>
        </Stack>
        {ticket.companyName && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ApartmentIcon sx={{ color: "primary.main" }} />
            <Typography align="left" color="primary.main" fontWeight="bold">
              {ticket.companyName}
            </Typography>
          </Stack>
        )}
        {ticket.department?.name && (
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupOutlinedIcon sx={{ color: "primary.main" }} />
            <Typography align="left" color="primary.main" fontWeight="bold">
              {ticket.department?.name}
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  if (user)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Typography align="left">{user.name}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonIcon sx={{ color: "text.secondary" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {user.username}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ApartmentIcon sx={{ color: "primary.main" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {user.companyName}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          fontWeight="bold"
        >
          <EmailIcon />
          <Typography align="left">{user.email}</Typography>
        </Stack>
      </Stack>
    );
  if (log)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonIcon sx={{ color: "text.secondary" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {log.user?.username || log.username}
          </Typography>
        </Stack>
        <Typography align="left">{capitalizeFirstLetter(log.model)}</Typography>
        <Typography align="left">{log.action}</Typography>
      </Stack>
    );
  if (department)
    return (
      <Typography align="left" color="text.secondary">
        {department.description}
      </Typography>
    );
  if (record)
    return (
      <Stack alignItems={"flex-start"} spacing={1}>
        <Typography align="left" color="text.secondary">
          {record.ip}/{record.prefix}
        </Typography>
        <Typography align="left" color="text.secondary" fontWeight="bold">
          {recordTypeObj[record.type]}
        </Typography>
      </Stack>
    );
  if (product)
    return (
      <Stack spacing={1}>
        <Stack alignItems={"flex-start"} direction={"row"} spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InventoryIcon sx={{ color: "text.secondary" }} />
            <Typography align="left" color="primary.main" fontWeight="bold">
              {product.group}
            </Typography>
          </Stack>
          {product.currency && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PaidIcon sx={{ color: "success.main" }} />
              <Typography align="left" fontWeight="bold" color="text.secondary">
                {`${product.price.toFixed(2)} ${product.currency}`}
              </Typography>
            </Stack>
          )}
        </Stack>
        {product.source === "SAP" && (
          <Stack alignItems={"flex-start"} direction={"row"} spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ProductionQuantityLimitsIcon sx={{ color: "text.secondary" }} />
              <Typography align="left" fontWeight="bold" color="text.secondary">
                x{product.totalQuantityInStock}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <BarChartIcon sx={{ color: "text.secondary" }} />
              <Typography align="left" fontWeight="bold" color="text.secondary">
                {product.totalAmountUsed}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  if (api)
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <PersonIcon sx={{ color: "primary.main" }} />
        <Typography align="left">{api.user?.name}</Typography>
      </Stack>
    );
  if (webhook)
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <LinkIcon sx={{ color: "primary.main" }} />
        <Typography
          align="left"
          maxWidth={300}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {webhook.url}
        </Typography>
      </Stack>
    );
  if (company)
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <BusinessCenter sx={{ color: "text.secondary" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {company.regNo}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <EmailIcon sx={{ color: "text.secondary" }} />
          <Typography align="left" fontWeight="bold" color="text.secondary">
            {company.emails.length}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Phone sx={{ color: "text.secondary" }} />
          <Typography align="left" fontWeight="bold" color="text.secondary">
            {company.phones.length}
          </Typography>
        </Stack>
      </Stack>
    );
  if (contact)
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EmailIcon sx={{ color: "primary" }} />
          <Typography align="left" color="primary.main" fontWeight="bold">
            {contact.email}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Phone sx={{ color: "text.secondary" }} />
          <Typography
            align="left"
            fontWeight="bold"
            color="text.secondary"
            sx={{
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {contact.phones.join(", ")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <SensorOccupied sx={{ color: "text.secondary" }} />
          <Typography
            align="left"
            fontWeight="bold"
            color="text.secondary"
            sx={{
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {contact.position}
          </Typography>
        </Stack>
      </Stack>
    );
}

function ItemCardFooter(props: ItemCardProps) {
  const { ticket, log, department, record, user } = props;
  const breakpoint = useBreakpoint();
  if (ticket && breakpoint.md) return <DateAndName date={ticket.date} />;
  else if (ticket)
    return (
      <Stack spacing={1}>
        {(ticket.city || ticket.location) && (
          <Stack direction="row" spacing={1} alignItems="center">
            <LocationOnIcon sx={{ color: "text.secondary" }} />
            <Typography
              align="left"
              maxWidth={200}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {ticket.city}
              {ticket.city && ticket.location && ", "}
              {ticket.location}
            </Typography>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" spacing={1} pl={0.2}>
          <DateAndName date={ticket.date} />

          <Stack direction="row" spacing={1} alignItems="center">
            <TicketStatus status={ticket.status} />
          </Stack>
        </Stack>
      </Stack>
    );

  if (log) return <DateAndName date={log.date} />;
  if (record) return <DateAndName date={record.date} />;
  if (department) return <DateAndName date={department.date} />;
  if (user) return <UserRoleChip role={user.role} />;
}

function DesktopCardSide(props: ItemCardProps) {
  const {
    ticket,
    log,
    user,
    department,
    product,
    api,
    webhook,
    company,
    contact,
  } = props;
  const { t } = useTranslation("Ticket");
  const { t: tLog } = useTranslation("Logs");
  const { t: tApi } = useTranslation("Api");

  return (
    <Stack height={1} justifyContent="space-between">
      <Box>
        {ticket && (
          <Typography align="right" fontWeight="bold">
            #{ticket.id}
          </Typography>
        )}
        {ticket?.service_call?.call_id && (
          <>
            <Typography align="right" fontSize={12} color="text.secondary">
              {t("ServiceCallID")}
            </Typography>
            <Typography align="right" fontSize={12} color="text.secondary">
              {ticket.service_call.call_id}
            </Typography>
          </>
        )}
        {product && (product.itemCode || product.extRef) && (
          <Typography align="right" fontWeight="bold">
            {product.itemCode || product.extRef}
          </Typography>
        )}
        {company && company.extRef && (
          <Typography align="right" fontWeight="bold">
            {company.extRef}
          </Typography>
        )}
        {contact && contact.extRef && (
          <Typography align="right" fontWeight="bold">
            {contact.extRef}
          </Typography>
        )}
        {api && (
          <Chip
            label={api.enabled ? tApi("Enabled") : tApi("Disabled")}
            color={api.enabled ? "success" : "default"}
          />
        )}
        {webhook && (
          <Chip
            label={webhook.enabled ? tApi("Enabled") : tApi("Disabled")}
            color={webhook.enabled ? "success" : "default"}
          />
        )}
      </Box>
    </Stack>
  );
}

function MobileCardSide(props: ItemCardProps) {
  const { ticket, product, api, webhook, company, contact } = props;
  const { t } = useTranslation("Ticket");
  const { t: tApi } = useTranslation("Api");

  return (
    <Stack height={1} justifyContent="space-between">
      <Box>
        {ticket && (
          <Typography align="right" fontWeight="bold">
            #{ticket.id}
          </Typography>
        )}
        {ticket?.service_call?.call_id && (
          <>
            <Typography align="right" fontSize={12} color="text.secondary">
              {t("ServiceCallID")}
            </Typography>
            <Typography align="right" fontSize={12} color="text.secondary">
              {ticket.service_call.call_id}
            </Typography>
          </>
        )}
        {product && (product.itemCode || product.extRef) && (
          <Typography align="right" fontWeight="bold">
            {product.itemCode || product.extRef}
          </Typography>
        )}
        {company && company.extRef && (
          <Typography align="right" fontWeight="bold">
            {company.extRef}
          </Typography>
        )}
        {contact && contact.extRef && (
          <Typography align="right" fontWeight="bold">
            {contact.extRef}
          </Typography>
        )}
        {api && (
          <Chip
            label={api.enabled ? tApi("Enabled") : tApi("Disabled")}
            color={api.enabled ? "success" : "default"}
          />
        )}
        {webhook && (
          <Chip
            label={webhook.enabled ? tApi("Enabled") : tApi("Disabled")}
            color={webhook.enabled ? "success" : "default"}
          />
        )}
      </Box>
      <Box textAlign="right">
        {ticket && <PriorityChip priority={ticket.priority} />}
      </Box>
    </Stack>
  );
}

export default function ItemCard(props: ItemCardProps) {
  const breakpoint = useBreakpoint();

  return (
    <Paper
      component={!breakpoint.lg && props.onClick ? ButtonBase : Paper}
      onClick={!breakpoint.lg ? props.onClick : undefined}
      sx={{ p: 1, bgcolor: "background.default", display: "block" }}
    >
      <Grid container spacing={2}>
        <Grid size={9}>
          <Stack spacing={1}>
            <ItemCardTitle {...props} />
            <Box
              sx={{
                ".MuiTypography-root": { fontSize: 12 },
                ".MuiChip-label": { fontSize: 12 },
              }}
              textAlign="left"
            >
              {breakpoint.md ? (
                <DesktopCardBody {...props} />
              ) : (
                <MobileCardBody {...props} />
              )}
            </Box>
            <Box
              sx={{
                ".MuiTypography-root": { fontSize: 12 },
                ".MuiChip-label": { fontSize: 12 },
              }}
              textAlign="left"
            >
              <ItemCardFooter {...props} />
            </Box>
          </Stack>
        </Grid>
        <Grid size={3}>
          {breakpoint.md ? (
            <DesktopCardSide {...props} />
          ) : (
            <MobileCardSide {...props} />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
}
